import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchAdminContactDetail,
  fetchAdminContacts,
  updateAdminContactStatus,
  type AdminContactDetail,
  type AdminContactSummary,
  type ContactStatus,
} from "../api";

const STATUSES: ContactStatus[] = ["new", "open", "resolved"];

function statusBadgeClass(status: ContactStatus): string {
  if (status === "resolved") return "admin-badge admin-badge-muted";
  if (status === "open") return "admin-badge admin-badge-live";
  return "admin-badge";
}

function formatTimestamp(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function ContactDetailModal({
  contactId,
  onClose,
  onUpdated,
}: {
  contactId: number;
  onClose: () => void;
  onUpdated: (contact: AdminContactDetail) => void;
}) {
  const [detail, setDetail] = useState<AdminContactDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await fetchAdminContactDetail(contactId);
        if (!cancelled) {
          setDetail(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  const changeStatus = async (status: ContactStatus) => {
    if (!detail || detail.status === status) return;
    setBusy(true);
    try {
      const updated = await updateAdminContactStatus(contactId, status);
      setDetail(updated);
      onUpdated(updated);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div
        className="admin-modal"
        style={{ width: "min(640px, 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="admin-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>

        <h2 className="admin-modal-title">
          Contact request #{contactId}
        </h2>

        {error ? <p className="admin-error">{error}</p> : null}

        {!detail ? (
          <p className="admin-page-sub">Loading…</p>
        ) : (
          <>
            <div className="admin-modal-body">
              <div className="admin-modal-info">
                <p>
                  <strong>Status</strong>
                  <span className={statusBadgeClass(detail.status)}>
                    {detail.status}
                  </span>
                </p>
                <p>
                  <strong>Subject</strong>
                  {detail.subject}
                </p>
                <p>
                  <strong>From</strong>
                  {detail.name} &lt;{detail.email}&gt;
                </p>
                {detail.username ? (
                  <p>
                    <strong>Logged-in user</strong>
                    {detail.username} (user #{detail.user_id})
                  </p>
                ) : (
                  <p>
                    <strong>Logged-in user</strong>
                    Guest
                  </p>
                )}
                <p>
                  <strong>Submitted</strong>
                  {formatTimestamp(detail.created_at)}
                </p>
                {detail.updated_at ? (
                  <p>
                    <strong>Updated</strong>
                    {formatTimestamp(detail.updated_at)}
                  </p>
                ) : null}
              </div>

              <div className="admin-modal-info">
                <p>
                  <strong>IP address</strong>
                  <code>{detail.ip_address || "—"}</code>
                </p>
                <p>
                  <strong>X-Forwarded-For</strong>
                  <code style={{ wordBreak: "break-all" }}>
                    {detail.x_forwarded_for || "—"}
                  </code>
                </p>
                <p>
                  <strong>User-Agent</strong>
                  <span style={{ wordBreak: "break-word" }}>
                    {detail.user_agent || "—"}
                  </span>
                </p>
              </div>

              <div>
                <strong style={{ color: "#8ba3b8", fontSize: "0.92rem" }}>
                  Message
                </strong>
                <pre
                  style={{
                    margin: "0.5rem 0 0",
                    padding: "0.75rem",
                    background: "rgba(0,0,0,0.25)",
                    borderRadius: 8,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "inherit",
                    fontSize: "0.92rem",
                    color: "#dce8f2",
                  }}
                >
                  {detail.message}
                </pre>
              </div>

              {detail.status === "resolved" ? (
                <div className="admin-modal-info">
                  <p>
                    <strong>Resolved</strong>
                    {formatTimestamp(detail.resolved_at)}
                  </p>
                  <p>
                    <strong>Resolved by</strong>
                    {detail.resolved_by_name || "—"}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="admin-modal-actions">
              {STATUSES.map((status) => (
                <button
                  key={status}
                  type="button"
                  className={`admin-btn${
                    detail.status === status
                      ? " admin-btn-primary"
                      : " admin-btn-ghost"
                  }`}
                  disabled={busy || detail.status === status}
                  onClick={() => void changeStatus(status)}
                >
                  Mark {status}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function ContactsPage() {
  const [contacts, setContacts] = useState<AdminContactSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ContactStatus | "all">("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchAdminContacts();
      setContacts(data);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return contacts;
    return contacts.filter((c) => c.status === statusFilter);
  }, [contacts, statusFilter]);

  const counts = useMemo(() => {
    const map: Record<ContactStatus, number> = { new: 0, open: 0, resolved: 0 };
    for (const c of contacts) map[c.status] += 1;
    return map;
  }, [contacts]);

  const onDetailUpdated = (updated: AdminContactDetail) => {
    setContacts((prev) =>
      prev.map((c) =>
        c.contact_id === updated.contact_id
          ? {
              contact_id: updated.contact_id,
              user_id: updated.user_id,
              status: updated.status,
              created_at: updated.created_at,
              updated_at: updated.updated_at,
              name: updated.name,
              email: updated.email,
              subject: updated.subject,
              username: updated.username,
              ip_address: updated.ip_address,
            }
          : c,
      ),
    );
  };

  return (
    <div>
      <h1 className="admin-page-title">Contact requests</h1>
      <p className="admin-page-sub">
        Messages submitted via the public contact form.
      </p>

      {error ? <p className="admin-error">{error}</p> : null}

      <div className="admin-toolbar">
        <button
          type="button"
          className="admin-btn admin-btn-ghost"
          onClick={() => void load()}
        >
          Refresh
        </button>
        <span className="admin-badge">{counts.new} new</span>
        <span className="admin-badge admin-badge-live">{counts.open} open</span>
        <span className="admin-badge admin-badge-muted">
          {counts.resolved} resolved
        </span>
      </div>

      <div className="admin-tabs" style={{ marginBottom: "1rem" }}>
        {(["all", ...STATUSES] as const).map((value) => (
          <button
            key={value}
            type="button"
            className={`admin-tab${statusFilter === value ? " active" : ""}`}
            onClick={() => setStatusFilter(value)}
          >
            {value === "all" ? "All" : value}
            {value === "all"
              ? ` (${contacts.length})`
              : ` (${counts[value]})`}
          </button>
        ))}
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>Subject</th>
              <th>From</th>
              <th>User</th>
              <th>IP</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7}>No contact requests.</td>
              </tr>
            ) : (
              filtered.map((c) => (
                <tr
                  key={c.contact_id}
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelectedId(c.contact_id)}
                >
                  <td>{c.contact_id}</td>
                  <td>
                    <span className={statusBadgeClass(c.status)}>
                      {c.status}
                    </span>
                  </td>
                  <td>{c.subject}</td>
                  <td>
                    {c.name}
                    <br />
                    <span style={{ fontSize: "0.85rem", color: "#8ba3b8" }}>
                      {c.email}
                    </span>
                  </td>
                  <td>{c.username || "—"}</td>
                  <td style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                    {c.ip_address || "—"}
                  </td>
                  <td>{formatTimestamp(c.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedId != null ? (
        <ContactDetailModal
          contactId={selectedId}
          onClose={() => setSelectedId(null)}
          onUpdated={onDetailUpdated}
        />
      ) : null}
    </div>
  );
}
